
import { Vue, Component, Watch } from 'vue-property-decorator'
import Column from '@/types/column'
import TableSettings from '@/types/tableSettings'
import ActiveFilters from '@/components/listview/ActiveFilters.vue'
import Pagination from '@/types/pagination'
import axios from 'axios'
import SurveyBulkUpdater from '@/components/listview/SurveyBulkUpdater.vue'
import PageHeader from '@/components/base/PageHeader.vue'
import SurveyForm from '@/types/surveyForm'
import ls from '@/ls'
import { can } from '@/globalFunctions'

@Component({
  methods: { can },
  components: { PageHeader, SurveyBulkUpdater, ActiveFilters }
})

export default class SurveyForms extends Vue {
  settings = {
    routeNames: { create: 'surveyFormCreate', show: 'surveyFormShow', edit: 'surveyFormEdit' },
    routeParamKey: 'surveyFormId',
    permissionNames: { show: 'surveys.forms.view', edit: 'surveys.forms.edit', delete: 'surveys.forms.delete' },
    functionNames: { getList: 'getSurveyFormList' },
    getterNames: { list: 'surveyFormList', loading: 'loadingSurveyForm' },
    localStorageName: 'surveyFormTableSettings'
  }

  columns:Column[] = [
    {
      title: 'Type',
      key: 'type',
      dataIndex: 'type_label',
      filters: [
        {
          text: 'Controle',
          value: 1
        },
        {
          text: 'Inspectie',
          value: 2
        },
        {
          text: 'Keuring',
          value: 3
        },
        {
          text: 'Oplevering',
          value: 4
        },
        {
          text: 'Bewijslast',
          value: 5
        },
        {
          text: 'Voorschouw',
          value: 6
        },
        {
          text: 'Keuringsdocument',
          value: 7
        },
        {
          text: 'Voorschouw/Oplevering',
          value: 8
        }
      ],
      sorter: false,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Formulier',
      key: 'form',
      scopedSlots: { customRender: 'form' },
      filters: [],
      sorter: false,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Omschrijving',
      key: 'description',
      dataIndex: 'description',
      filters: [],
      sorter: false,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Beschikbaar bij bouwsoort',
      key: 'construction_types',
      scopedSlots: { customRender: 'construction_types' },
      filters: [
        {
          text: 'Nieuwbouw',
          value: 1
        },
        {
          text: 'Renovatie',
          value: 2
        },
        {
          text: 'Onderhoud',
          value: 3
        }
      ],
      sorter: false,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Actief',
      key: 'active',
      scopedSlots: { customRender: 'active' },
      filters: [
        {
          text: 'Ja',
          value: 1
        },
        {
          text: 'Nee',
          value: 0
        }
      ],
      sorter: false,
      sortDirections: ['ascend', 'descend']
    },
    {
      title: 'Actie\'s',
      key: 'action',
      scopedSlots: { customRender: 'action' }
    }
  ]

  tableSettings:TableSettings = ls.get(this.settings.localStorageName) || {
    pagination: { current: 1, pageSize: 25 },
    showSizeChanger: true,
    pageSizeOptions: ['25', '50', '100'],
    activeFilters: null,
    sort: null,
    search: null
  }

  selectedRowKeys:number[] = []
  filtersLoaded = false

  mounted () : void {
    this.$store.dispatch(this.settings.functionNames.getList, this.tableSettings)
    this.getFilters()
    this.onTableSettingsChange()
  }

  reloadList () : void {
    this.selectedRowKeys = []
    this.$store.dispatch(this.settings.functionNames.getList, this.tableSettings)
  }

  async getFilters () : Promise<void> {
    this.filtersLoaded = true
  }

  handleTableChange (pagination:{ current:number, pageSize:number }, filters:{[key: string]: number[]|string[]}, sort:{ columnKey:string, order:string }) : void {
    // Remove key from filters if filter is an empty object.
    if (filters && Object.keys(filters).length) {
      Object.keys(filters).forEach((key) => {
        if (!filters[key] || !filters[key].length) {
          delete filters[key]

          this.columns.forEach((column:Column) => {
            if (column.key === key) {
              column.filteredValue = null
            }
          })
        }
      })
    }

    // Set pagination, filters and sorting on tableSettings.
    this.tableSettings.pagination = pagination
    this.tableSettings.activeFilters = filters
    this.tableSettings.sort = sort.order ? sort : null

    // Get list based on filters and search term.
    this.$store.dispatch(this.settings.functionNames.getList, this.tableSettings)
  }

  handleSearchChange () : void {
    // Set page to 1 because of new search but retain filters and sorting.
    this.tableSettings.pagination.current = 1

    // Get list based on filters and search term.
    this.$store.dispatch(this.settings.functionNames.getList, this.tableSettings)
  }

  // Required for ActiveFilters component
  updateFilters (filters: {[key: string]: number[]|string[]|boolean[]}) : void {
    const { columns } = this

    columns.forEach((column:Column) => {
      column.filteredValue = column.key in filters ? filters[column.key] : []
    })

    this.tableSettings.activeFilters = Object.assign({}, filters)
    this.$store.dispatch(this.settings.functionNames.getList, this.tableSettings)
  }

  onRowClick (rowData:{id:number}) : { on: { click: () => void }} {
    return {
      on: {
        click: () => {
          const keyboard = window.event as KeyboardEvent
          if (can(this.settings.permissionNames.edit)) {
            const route = this.$router.resolve({ name: this.settings.routeNames.edit, params: { [this.settings.routeParamKey]: rowData.id.toString() } })
            window.open(route.href, keyboard.ctrlKey ? '_blank' : '_self')
          } else if (can(this.settings.permissionNames.show)) {
            const route = this.$router.resolve({ name: this.settings.routeNames.show, params: { [this.settings.routeParamKey]: rowData.id.toString() } })
            window.open(route.href, keyboard.ctrlKey ? '_blank' : '_self')
          }
        }
      }
    }
  }

  onSelectChange (selectedRowKeys:number[]) : void {
    this.selectedRowKeys = selectedRowKeys
  }

  navigateTo (routeName:string, id:number|undefined) : void {
    if (id) {
      this.$router.push({ name: routeName, params: { [this.settings.routeParamKey]: id.toString() } })
    } else {
      this.$router.push({ name: routeName })
    }
  }

  previousPage () : void {
    this.$router.go(-1)
  }

  get itemList () : { data: SurveyForm[], meta: { per_page:string, total:number } } {
    return this.$store.getters[this.settings.getterNames.list]
  }

  get pagination () : Pagination {
    return {
      current: this.tableSettings.pagination.current,
      defaultPageSize: this.tableSettings.pagination.pageSize,
      pageSize: parseInt(this.itemList.meta.per_page),
      total: this.itemList.meta.total,
      showTotal: (total, range) => `Keuringsformulier ${range[0]} t/m ${range[1]} van de ${total}`,
      showSizeChanger: this.tableSettings.showSizeChanger,
      pageSizeOptions: this.tableSettings.pageSizeOptions
    }
  }

  get loading () : boolean {
    return this.$store.getters[this.settings.getterNames.loading].list
  }

  get navigationCollapsed () : boolean {
    return this.$store.getters.navigationCollapsed
  }

  get screenWidth () : number {
    return screen.width
  }

  @Watch('tableSettings', { deep: true })
  onTableSettingsChange () : void {
    // On tableSettings change, store them in local storage.
    ls.set(this.settings.localStorageName, this.tableSettings)

    axios.post(process.env.VUE_APP_API_URL + '/users/admin/settings', {
      settingName: this.settings.localStorageName,
      settingValue: this.tableSettings
    })

    // Set the current active filters on the columns.
    this.columns.forEach((column:Column) => {
      // Set active filters.
      if (this.tableSettings.activeFilters && column.key in this.tableSettings.activeFilters) {
        column.filteredValue = this.tableSettings.activeFilters[column.key]
      }

      // Set active sort order.
      if (this.tableSettings.sort && column.key === this.tableSettings.sort.columnKey) {
        column.defaultSortOrder = this.tableSettings.sort.order
        column.sortOrder = this.tableSettings.sort.order
      } else {
        column.sortOrder = false
      }
    })
  }
}
